import * as React from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import mStyle from './FlexLayout.module.scss';
import { JoinClassName } from '../../../utils/string';
import { FlexLayoutProps } from '../../../constants';

// justify/align 문자열 → CSS 클래스 매핑.
// 미지정은 inherit — justify-content/align-items는 상속되지 않는 속성이라
// 명시적 inherit이어야 기존 인라인 스타일과 같은 상속 체인이 유지된다.
const JUSTIFY_CLASS: { [key: string]: string } = {
  LEFT: mStyle.justify__start,
  START: mStyle.justify__start,
  RIGHT: mStyle.justify__end,
  END: mStyle.justify__end,
  INIT: mStyle.justify__initial,
  INITIAL: mStyle.justify__initial,
};

const ALIGN_CLASS: { [key: string]: string } = {
  LEFT: mStyle.align__start,
  START: mStyle.align__start,
  RIGHT: mStyle.align__end,
  END: mStyle.align__end,
  INIT: mStyle.align__initial,
  INITIAL: mStyle.align__initial,
};

const getJustifyClass = (value?: string) => {
  if (!value) return mStyle.justify__inherit;
  return JUSTIFY_CLASS[value.toUpperCase()] || mStyle.justify__center;
};

const getAlignClass = (value?: string) => {
  if (!value) return mStyle.align__inherit;
  return ALIGN_CLASS[value.toUpperCase()] || mStyle.align__center;
};

const renderTrackHorizontal = (props: any) => (
  <div {...props} className="track-horizontal" />
);

const renderTrackVertical = (props: any) => (
  <div {...props} className="track-vertical" />
);

const renderThumbHorizontal = (props: any) => (
  <div {...props} className="thumb-horizontal" />
);

const renderThumbVertical = (props: any) => (
  <div {...props} className="thumb-vertical" />
);

/**
 * 동적 레이아웃
 *
 * @remarks
 * 페이지의 모든 뷰는 이 레이아웃을 기본으로 사용해야 합니다.
 * 1920x1080 해상도를 기본으로 하되 더 작거나 큰 해상도를 고려하도록 상하 여백은 paading보다 height를 권장합니다.
 *
 * 사용 빈도가 매우 높아(2만+ 인스턴스) 렌더 비용을 최소화한 구조:
 * div를 직접 렌더하고(중간 컴포넌트 없음), 이벤트 핸들러는 지정된 것만 붙이며,
 * justify/align/기본 flex는 인라인 스타일 대신 CSS 클래스로 처리한다.
 */
export class FlexLayout extends React.PureComponent<FlexLayoutProps> {
  public static readonly DOM_NAME = 'FlexLayout';

  private lastTapTime = 0;

  private handleClick = () => {
    this.props.onClick && this.props.onClick();
  };

  private handleTouchStart = () => {
    const now = new Date().getTime();
    if (now - this.lastTapTime < 300) {
      this.props.onDoubleClick && this.props.onDoubleClick();
    }
    this.lastTapTime = now;
  };

  private handleDoubleClick = () => {
    this.props.onDoubleClick && this.props.onDoubleClick();
  };

  private handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    this.props.onMouseMove && this.props.onMouseMove(e);
  };

  private handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    this.props.onMouseLeave && this.props.onMouseLeave(e);
  };

  private handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    this.props.onWheel && this.props.onWheel(e);
  };

  private handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    this.props.onFocus && this.props.onFocus(e);
  };

  private handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    this.props.onUnFocus && this.props.onUnFocus(e);
  };

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    this.props.onKeyDown && this.props.onKeyDown(e);
  };

  render() {
    const {
      refs,
      role,
      elField,
      elOption,
      elCell,
      elRow,
      elGrid,
      isVertical,
      children,
      className,
      isScroll,
      weight,
      size,
      minHeight,
      justify,
      align,
      style,
      tabindex,
      disabled,
      disabledText,
      ellipsis,
      onClick,
      onDoubleClick,
      onMouseMove,
      onMouseLeave,
      onWheel,
      onFocus,
      onUnFocus,
      onKeyDown,
    } = this.props;

    // style은 any로 선언되어 페이지에서 `cond && {...}` 패턴의 false 등
    // 객체가 아닌 값이 넘어올 수 있다. DefaultLayout 위임 시절에는 스프레드가
    // 이를 조용히 삼켰으므로 같은 동작을 유지한다 (div에 직접 전달하면 throw).
    const safeStyle = typeof style === 'object' ? style : undefined;

    const useDefaultFlex = !weight && !size;

    const baseClassName = JoinClassName.make([
      mStyle.container,
      className,
      getJustifyClass(justify),
      getAlignClass(align),
      isVertical ? mStyle.direction__vertical : null,
      disabled ? mStyle.disabled : null,
    ]);

    const content = (
      <React.Fragment>
        {disabled && disabledText && (
          <div className={mStyle.disabled__text}>{disabledText}</div>
        )}
        {ellipsis ? (
          <div className={mStyle.ellipsis} style={{ WebkitLineClamp: ellipsis }}>{children}</div>
        ) : children}
      </React.Fragment>
    );

    if (isScroll) {
      const cWeight = useDefaultFlex ? 1 : weight;
      const oMinHeight = minHeight ? { minHeight } : {};

      return (
        <Scrollbars
          renderTrackHorizontal={renderTrackHorizontal}
          renderTrackVertical={renderTrackVertical}
          renderThumbHorizontal={renderThumbHorizontal}
          renderThumbVertical={renderThumbVertical}
          autoHide
          autoHideTimeout={1000}
          autoHideDuration={200}
          style={
            // eslint-disable-next-line no-nested-ternary
            cWeight
              ? { flex: cWeight, ...oMinHeight }
              : size
                ? { flex: `${size}px 0 0`, ...oMinHeight }
                : {}
          }
        >
          <div
            ref={refs}
            role={role}
            data-el-field={elField}
            data-el-option={elOption}
            data-el-cell={elCell}
            data-el-row={elRow}
            data-el-grid={elGrid}
            className={baseClassName}
            tabIndex={tabindex}
            style={safeStyle}
            onClick={onClick ? this.handleClick : undefined}
            onFocus={onFocus ? this.handleFocus : undefined}
            onBlur={onUnFocus ? this.handleBlur : undefined}
            onKeyDown={onKeyDown ? this.handleKeyDown : undefined}
          >
            {content}
          </div>
        </Scrollbars>
      );
    }

    // 기본 flex:1은 CSS 클래스로, weight/size/minHeight 지정시에만 인라인 스타일 생성
    let divStyle = safeStyle;
    if (!useDefaultFlex || minHeight) {
      const flexStyle: any = {};
      if (weight) flexStyle.flex = weight;
      else if (size) flexStyle.flex = `${size}px 0 0`;
      if (minHeight) flexStyle.minHeight = minHeight;
      divStyle = safeStyle ? { ...flexStyle, ...safeStyle } : flexStyle;
    }

    return (
      <div
        ref={refs}
        role={role}
        data-el-field={elField}
        data-el-option={elOption}
        data-el-cell={elCell}
        data-el-row={elRow}
        data-el-grid={elGrid}
        className={useDefaultFlex
          ? JoinClassName.make([baseClassName, mStyle.flex__default])
          : baseClassName}
        tabIndex={tabindex}
        style={divStyle}
        onClick={onClick ? this.handleClick : undefined}
        onTouchStart={onDoubleClick ? this.handleTouchStart : undefined}
        onDoubleClick={onDoubleClick ? this.handleDoubleClick : undefined}
        onMouseMove={onMouseMove ? this.handleMouseMove : undefined}
        onMouseLeave={onMouseLeave ? this.handleMouseLeave : undefined}
        onWheel={onWheel ? this.handleWheel : undefined}
        onFocus={onFocus ? this.handleFocus : undefined}
        onBlur={onUnFocus ? this.handleBlur : undefined}
        onKeyDown={onKeyDown ? this.handleKeyDown : undefined}
      >
        {content}
      </div>
    );
  }
}
